import { Target } from '@/types/c2/c2.types';
import { Coordinates } from '@/types/commons/commons.types';
import { DroneAlertLevelEnum } from '@/types/data/data.types';
import { FeatureTypeEnum, SensorUniqueKeys } from '@/types/map.types';
import { ReplayEvent } from '@/types/replay/replay.types';
import { ReliabilityEnum } from '@/types/sensor/identification.types';
import { TargetLocation } from '@/types/sensor/sensor.types';
import { AutomatonSectionUniqueKeys, TrackLight } from '@/types/sensor/sensors';

export enum TooltipTypeEnum {
  AZIMUTH_AND_DISTANCE = 'AZIMUTH_AND_DISTANCE',
  EVENT = 'EVENT',
  LABEL = 'LABEL',
  NO_FLY_ZONE = 'NO_FLY_ZONE',
  PERIMETER_PORTAL = 'PERIMETER_PORTAL',
  PERMANENT = 'PERMANENT',
  POI = 'POI ',
  STATISTIC = 'STATISTIC',
  TARGET = 'TARGET',
}

export const FeatureToTooltipTypeMap: Partial<Record<FeatureTypeEnum, TooltipTypeEnum>> = {
  [FeatureTypeEnum.EVENT_ICON]: TooltipTypeEnum.EVENT,
  [FeatureTypeEnum.SENSOR_ICON]: TooltipTypeEnum.LABEL,
  [FeatureTypeEnum.SENSOR_LABEL]: TooltipTypeEnum.LABEL,
  [FeatureTypeEnum.AUTOMATON_SECTION_LABEL]: TooltipTypeEnum.LABEL,
  [FeatureTypeEnum.NO_FLY_ZONE]: TooltipTypeEnum.NO_FLY_ZONE,
  [FeatureTypeEnum.NO_FLY_ZONE_3D]: TooltipTypeEnum.NO_FLY_ZONE,
  [FeatureTypeEnum.PERIMETER_PORTAL]: TooltipTypeEnum.PERIMETER_PORTAL,
  [FeatureTypeEnum.POI_ICON]: TooltipTypeEnum.POI,
  [FeatureTypeEnum.STATISTIC_POINT]: TooltipTypeEnum.STATISTIC,
  [FeatureTypeEnum.TARGET_ICON]: TooltipTypeEnum.TARGET,
} as const;

type FeatureToTooltipTypeMapType = typeof FeatureToTooltipTypeMap;
export function getTooltipTypeForFeature<T extends FeatureTypeEnum>(featureType: T): FeatureToTooltipTypeMapType[T] {
  return FeatureToTooltipTypeMap[featureType];
}

interface AzimuthAndDistanceTooltipData {
  tooltipPosition: Coordinates;
}

interface NoFlyZoneTooltipData {
  name: string;
  level: DroneAlertLevelEnum;
}

interface StatisticPointTooltipData {
  displayId: string | null;
  id: string;
  reliability: ReliabilityEnum;
  startTime: string;
  endTime: string;
  maxHeight: number | null;
  trackIds: TrackLight[];
  worstAlertLevel: DroneAlertLevelEnum | null;
  altitude: number | null;
}

export type TooltipDataType =
  | {
      type: TooltipTypeEnum.AZIMUTH_AND_DISTANCE;
      data: AzimuthAndDistanceTooltipData;
    }
  | {
      type: TooltipTypeEnum.EVENT;
      data: ReplayEvent;
    }
  | {
      type: TooltipTypeEnum.LABEL;
      data: AutomatonSectionUniqueKeys | SensorUniqueKeys;
    }
  | {
      type: TooltipTypeEnum.NO_FLY_ZONE;
      data: NoFlyZoneTooltipData;
    }
  | {
      type: TooltipTypeEnum.PERMANENT;
      data: never;
    }
  | {
      type: TooltipTypeEnum.POI;
      data: { name: string };
    }
  | {
      type: TooltipTypeEnum.PERIMETER_PORTAL;
      data: string;
    }
  | {
      type: TooltipTypeEnum.STATISTIC;
      data: StatisticPointTooltipData;
    }
  | {
      type: TooltipTypeEnum.TARGET;
      data: Target<TargetLocation>;
    };

export type TooltipComponentProps<T extends TooltipTypeEnum> = {
  data: Extract<TooltipDataType, { type: T }>['data'];
};
